import type { GetServerSidePropsContext } from 'next';
import { useConfig } from 'shared/config/ConfigContext';
import { fetchVariants } from 'shared/experiment/variants';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { SWRConfig } from 'swr';
import { fetchAvailabilityPerDateSsr, getCurrentCancellationPolicy } from '@/api/apiInternal';
import { fetchAvailabilityForPrivateForServer } from '@/api/availabilityApi';
import { fetchHostBadges, fetchObject } from '@/api/objectApi';
import { checkValidDatesSsr } from '@/api/transactionApiInternal';
import type { ObjectData } from '@/domain';
import { populateProfile } from '@/ducks/profile/profileActions';
import { ExperimentProvider } from '@/hooks/experiments';
import { createFetchKey as createAvailabilityInfoKey } from '@/hooks/useAvailabilityForPrivate';
import { DATA_KEY as AVAILABILITY_KEY } from '@/hooks/useAvailabilityPerDate';
import { DATA_KEY as CURRENT_CANCELLATION_POLICY_KEY } from '@/hooks/useCurrentCancellationPolicy';
import { DATA_KEY as HOST_BADGES_DATA_KEY } from '@/hooks/useHostBadges';
import { USE_OBJECT_KEY } from '@/hooks/useObject';
import { imageDataToMetaTags } from '@/lib/helpers/imageDataToMetaTags';
import { truncateWithEllipsis } from '@/lib/helpers/strings';
import type { CancellationPolicy } from '@/models/cancellationPolicy';
import { ObjectPage } from '@/scenes/objectPage/ObjectPage';
import { clientConfig, config } from '@/server/config';
import { buildCommonProps, sanitizeCommonProps } from '@/server/middleware';
import { wrapper } from '@/store/store';
import type { ObjectPageBaseProps } from '@/types/common';
import { replaceHtmlNewlines } from '@/utils/formatting';
import { generateStructuredData } from '@/utils/generateStructuredData';

interface AdProps extends ObjectPageBaseProps {
    experiments: Record<string, string>;
}

const Ad = (props: AdProps) => {
    wrapper.useHydration(props);
    const { baseUrl } = useConfig();
    const objectData = props.swrFallback[USE_OBJECT_KEY] as ObjectData;
    const imageMetaTags = imageDataToMetaTags(objectData.images?.[0]);
    const truncatedDescription = truncateWithEllipsis(replaceHtmlNewlines(objectData.residenceDescription), 200) ?? '';
    const structuredData = generateStructuredData(objectData);

    return (
        <ExperimentProvider experiments={props.experiments}>
            <SWRConfig value={{ fallback: props.swrFallback }}>
                <Head
                    title={`${objectData.heading} | FINN reise`}
                    description={truncatedDescription}
                    canonical={`${baseUrl}/reise/feriehus-hytteutleie/ad.html?finnkode=${objectData.adId}`}
                    imageUrl={imageMetaTags ? imageMetaTags.og.url : undefined}>
                    {imageMetaTags !== null && (
                        <>
                            <meta property="og:image:alt" content={imageMetaTags.og.description} />
                            <meta name="twitter:image" content={imageMetaTags.twitter.url} />
                        </>
                    )}
                    {(objectData.itemType === 'tjvr' || objectData.itemType === 'classified') && (
                        <meta key="robots" name="robots" content="index, follow" />
                    )}
                    {structuredData && objectData.itemType !== 'import' && (
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
                    )}
                </Head>
                <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie">
                    <ObjectPage deviceType={props.deviceType} loginId={props.loginId} validDates={props.validDates} />
                </Main>
            </SWRConfig>
        </ExperimentProvider>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }: GetServerSidePropsContext) => {
    const commonProps = await buildCommonProps(req, res, query);
    const isLoggedIn = commonProps.loginId !== null && commonProps.loginId !== undefined;

    const experiments = await fetchVariants(req, res, 'travel-fhh-web', config.get('experimentsApiUrl'), [
        'travel-fhh-tjvr-object-page-drop-off-reduction',
    ]);

    const adId = typeof query?.finnkode === 'string' ? query.finnkode : '';
    const objectData = await fetchObject(adId, commonProps.authCookies, req.cookies.USERID)
        .then((data) => data)
        .catch(() => null);

    if (!objectData) {
        return { notFound: true };
    }

    let hostBadgesData = {};
    if (objectData.itemType === 'tjvr' && objectData.loginId && isLoggedIn) {
        hostBadgesData = (await fetchHostBadges(objectData.loginId, commonProps.authCookies, req.cookies.USERID).catch(() => null)) || {};
    }

    const availabilityInfoKey = createAvailabilityInfoKey(Number(adId));
    const isPrivateAd = objectData.itemType === 'tjvr' || objectData.itemType === 'classified';
    const availabilityForPrivate = isPrivateAd
        ? await fetchAvailabilityForPrivateForServer({ adId: Number(adId) }).catch(() => null)
        : null;
    const availabilityPerDate = await fetchAvailabilityPerDateSsr({ itemId: Number(adId) });

    let currentCancellationPolicy: CancellationPolicy | null = null;
    if (objectData.itemType === 'tjvr') {
        currentCancellationPolicy = await getCurrentCancellationPolicy(Number(adId));
    }

    const clientObjectData =
        isPrivateAd && !isLoggedIn
            ? {
                  ...objectData,
                  owner: {
                      name: '',
                      url: null,
                      email: null,
                      mobile: null,
                      phone: null,
                      deeplink: null,
                      hasPhoneNumber: false,
                  },
              }
            : objectData;
    const swrFallback = {
        [availabilityInfoKey]: availabilityForPrivate,
        [AVAILABILITY_KEY]: availabilityPerDate,
        [USE_OBJECT_KEY]: clientObjectData,
        [HOST_BADGES_DATA_KEY]: hostBadgesData,
        [CURRENT_CANCELLATION_POLICY_KEY]: currentCancellationPolicy,
    };

    let overrideLoginProps = {};
    if (process.env.MODULE_TEST === 'true') {
        overrideLoginProps = { loginId: 123456789 };
        await store.dispatch(populateProfile(config.get('profileReadUrl'), 123456789));
    } else if (isPrivateAd && isLoggedIn) {
        await store.dispatch(populateProfile(config.get('profileReadUrl'), objectData.loginId));
    }

    const from = typeof query?.fra === 'string' ? query.fra : null;
    const to = typeof query?.til === 'string' ? query.til : null;
    const validDates = from && to ? await checkValidDatesSsr(adId, from, to) : false;

    return {
        props: {
            config: clientConfig,
            ...sanitizeCommonProps(commonProps),
            ...overrideLoginProps,
            swrFallback,
            validDates,
            experiments: experiments ?? {},
        },
    };
});

export default Ad;
